import { client } from "../../api/client.ts";
import {
    ApiAccessBoundary,
    type ApiAccessRedirect,
    useGetOnboardingRedirectPaths,
    useGetSetupUrl,
    useOnboardingRouteInfo,
    useTenants
} from "@sg-protect/shell";
import { type PropsWithChildren, useMemo } from "react";

export function ModuleApiAccessBoundary({ children }: PropsWithChildren) {
    const onboardingRedirects = useGetOnboardingRedirectPaths();
    const hasPrimaryTenant = useTenants().tenants?.primaryTenant != null;
    const { isOnboardingStep } = useOnboardingRouteInfo();
    const consentRedirectUrl = useGetSetupUrl("governance");
    // Not in setup, whose requests send the tenant being set up. Without a primary tenant, notConsented leads to the
    // zero state.
    const isInvalidTenantRedirected = hasPrimaryTenant && !isOnboardingStep;

    // openapi-fetch calls onResponse from the last middleware to the first, so when a 403 body carries more than one
    // reason, the last one listed here wins.
    const redirects = useMemo<ApiAccessRedirect[]>(
        () => [
            { reason: "notConsented", to: onboardingRedirects.notConsented },
            { reason: "noSgWorkspaceProductAccess", to: onboardingRedirects.noAccess },
            { reason: "missingShareGateLicense", to: onboardingRedirects.missingLicense },
            ...(isInvalidTenantRedirected
                ? [{ reason: "invalidSgTenant", to: consentRedirectUrl } satisfies ApiAccessRedirect]
                : [])
        ],
        [
            onboardingRedirects.notConsented,
            onboardingRedirects.noAccess,
            onboardingRedirects.missingLicense,
            isInvalidTenantRedirected,
            consentRedirectUrl
        ]
    );

    return (
        <ApiAccessBoundary redirects={redirects} client={client}>
            {children}
        </ApiAccessBoundary>
    );
}
